import { useEffect, useState, useCallback, Fragment } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ExternalLink, RefreshCw, Pencil, Plus, X, ShieldAlert, Clock, History, FlaskConical, Check,
} from "lucide-react";
import {
  STATE_TONE, STATE_LABEL, INTEGRATION_LABEL, SOURCE_LABEL, fmtMoney, stateOf, freshnessText, coverageText,
} from "@/lib/billingStatus";

/* External Provider Billing & Subscription Monitor (2026-09) — Administration
   → Billing & Subscriptions. Tracks Interakt/OpenAI/Railway/Exotel/
   ElevenLabs billing health in ONE generic model (backend: provider_
   billing.py); every provider supports full manual tracking, auto-fetch
   only ever enriches the fields a provider's API actually exposes.

   Stabilization round: each card shows the SEPARATED status model (integration
   health, data availability, manual vs automatic + freshness, plan/budget,
   spend, month-end estimate, genuinely remaining balance, promotional credits,
   risk) instead of one overloaded badge, so "Unknown" is never read as
   "Failed" and a zero promotional-credit balance is never presented as an
   emergency. */

const fmtPercent = (usage, limit) => (usage == null || !limit) ? null : Math.max(0, Math.round(100 * (1 - usage / limit)));

export const EditProviderDialog = ({ doc, onClose, onSaved }) => {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!doc) { setForm(null); return; }
    setForm({
      enabled: doc.enabled, plan_name: doc.plan_name || "", currency: doc.currency || "",
      monthly_cost: doc.monthly_cost ?? "", auto_renew: doc.auto_renew,
      current_balance: doc.current_balance ?? "", current_usage: doc.current_usage ?? "",
      usage_limit: doc.usage_limit ?? "", renewal_date: doc.renewal_date ? doc.renewal_date.slice(0, 10) : "",
      validity_date: doc.validity_date ? doc.validity_date.slice(0, 10) : "",
      low_balance_threshold: doc.low_balance_threshold ?? "",
      critical_balance_threshold: doc.critical_balance_threshold ?? "",
      low_remaining_percent_threshold: doc.low_remaining_percent_threshold ?? "",
      critical_remaining_percent_threshold: doc.critical_remaining_percent_threshold ?? "",
      warning_days_before: doc.warning_days_before ?? 7, critical_days_before: doc.critical_days_before ?? 1,
      billing_url: doc.billing_url || "", notes: doc.notes || "",
      status_override: (doc.manual_fields || {}).status_override || "",
      prepaid_credit: (doc.manual_fields || {}).prepaid_credit ?? "",
      monthly_budget: doc.monthly_budget ?? "",
      budget_warning_percent: doc.budget_warning_percent ?? 80,
      budget_critical_percent: doc.budget_critical_percent ?? 100,
      manual_stale_after_days: doc.manual_stale_after_days ?? 7,
      promotional_credits: doc.promotional_credits ?? "",
      tracking_mode: doc.tracking_mode || "manual",
      confirm_manual_data: false,
    });
  }, [doc]);

  if (!doc || !form) return null;
  const num = (v) => (v === "" || v === null || v === undefined) ? null : Number(v);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const a = doc.assessment || {};
  const hasApi = (a.integration || {}).health !== "not_applicable";
  const isPromoProvider = !!((a.promotional_credits || {}).meaning);
  const isCycleProvider = ["openai", "railway"].includes(doc.provider);

  const save = async () => {
    try {
      await api.put(`/admin/billing/providers/${doc.provider}`, {
        enabled: form.enabled, plan_name: form.plan_name || null, currency: form.currency || null,
        monthly_cost: num(form.monthly_cost), auto_renew: form.auto_renew,
        current_balance: num(form.current_balance), current_usage: num(form.current_usage),
        usage_limit: num(form.usage_limit),
        renewal_date: form.renewal_date || "", validity_date: form.validity_date || "",
        low_balance_threshold: num(form.low_balance_threshold),
        critical_balance_threshold: num(form.critical_balance_threshold),
        low_remaining_percent_threshold: num(form.low_remaining_percent_threshold),
        critical_remaining_percent_threshold: num(form.critical_remaining_percent_threshold),
        warning_days_before: num(form.warning_days_before), critical_days_before: num(form.critical_days_before),
        billing_url: form.billing_url || null, notes: form.notes,
        status_override: form.status_override || "",
        prepaid_credit: form.prepaid_credit === "" ? null : num(form.prepaid_credit),
        monthly_budget: num(form.monthly_budget),
        budget_warning_percent: num(form.budget_warning_percent),
        budget_critical_percent: num(form.budget_critical_percent),
        manual_stale_after_days: num(form.manual_stale_after_days),
        promotional_credits: num(form.promotional_credits),
        tracking_mode: form.tracking_mode,
        confirm_manual_data: form.confirm_manual_data || undefined,
      });
      toast.success(`${doc.display_name} updated`);
      onSaved();
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{doc.display_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Tracking enabled</Label>
            <Switch data-testid="billing-edit-enabled" checked={form.enabled} onCheckedChange={set("enabled")} />
          </div>
          {hasApi && (
            <div className="space-y-1">
              <Label className="text-xs">Data mode</Label>
              <Select value={form.tracking_mode} onValueChange={set("tracking_mode")}>
                <SelectTrigger data-testid="billing-edit-tracking-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Automatic - check the provider API</SelectItem>
                  <SelectItem value="manual">Manual - no API calls, enter figures by hand</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                {(a.integration || {}).required_scopes?.length
                  ? `Automatic mode needs an API key with: ${a.integration.required_scopes.join(", ")}. Use Manual until such a key is saved.`
                  : "Manual mode never calls the provider; the figures below are what the status is based on."}
              </p>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Plan / tier</Label>
              <Input data-testid="billing-edit-plan" value={form.plan_name} onChange={(e) => set("plan_name")(e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Currency</Label>
              <Input value={form.currency} onChange={(e) => set("currency")(e.target.value)} placeholder="usd / INR" /></div>
            <div className="space-y-1"><Label className="text-xs">{["openai", "railway"].includes(doc.provider) ? "Spend this period (manual override)" : "Monthly cost / spend"}</Label>
              <Input type="number" value={form.monthly_cost} onChange={(e) => set("monthly_cost")(e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Auto-renew</Label>
              <Select value={form.auto_renew === null ? "unknown" : String(form.auto_renew)}
                onValueChange={(v) => set("auto_renew")(v === "unknown" ? null : v === "true")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="true">ON</SelectItem><SelectItem value="false">OFF</SelectItem><SelectItem value="unknown">Unknown</SelectItem></SelectContent>
              </Select></div>
            {!isPromoProvider && (
              <div className="space-y-1"><Label className="text-xs">Remaining balance / credits (only if the provider dashboard states one)</Label>
                <Input data-testid="billing-edit-balance" type="number" value={form.current_balance} onChange={(e) => set("current_balance")(e.target.value)} /></div>
            )}
            {isPromoProvider && (
              <div className="space-y-1"><Label className="text-xs">Unused promotional credits (manual, optional)</Label>
                <Input data-testid="billing-edit-promo" type="number" value={form.promotional_credits} onChange={(e) => set("promotional_credits")(e.target.value)} />
                <p className="text-[11px] text-muted-foreground">Informational only - never used as a low-balance trigger.</p></div>
            )}
            <div className="space-y-1"><Label className="text-xs">Current usage</Label>
              <Input type="number" value={form.current_usage} onChange={(e) => set("current_usage")(e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Usage limit / quota</Label>
              <Input type="number" value={form.usage_limit} onChange={(e) => set("usage_limit")(e.target.value)} /></div>
            {doc.provider === "openai" && (
              <div className="space-y-1"><Label className="text-xs">Prepaid credit purchased (manual, for an ESTIMATED remaining balance)</Label>
                <Input type="number" value={form.prepaid_credit} onChange={(e) => set("prepaid_credit")(e.target.value)} /></div>
            )}
            <div className="space-y-1"><Label className="text-xs">{isCycleProvider ? "Renewal date (anchors the monthly budget cycle)" : "Renewal date"}</Label>
              <Input data-testid="billing-edit-renewal" type="date" value={form.renewal_date} onChange={(e) => set("renewal_date")(e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Validity date</Label>
              <Input type="date" value={form.validity_date} onChange={(e) => set("validity_date")(e.target.value)} /></div>
          </div>

          <div className="border-t pt-3 space-y-2">
            <p className="text-xs font-medium">Budget</p>
            <p className="text-[11px] text-muted-foreground">
              A monthly budget lets the status be derived from actual spend. Without one the status is
              "Unknown - no budget set" - not a failure.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs">Monthly budget</Label>
                <Input data-testid="billing-edit-budget" type="number" value={form.monthly_budget} onChange={(e) => set("monthly_budget")(e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">Warn at % of budget</Label>
                <Input data-testid="billing-edit-budget-warn" type="number" value={form.budget_warning_percent} onChange={(e) => set("budget_warning_percent")(e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">Critical at % of budget</Label>
                <Input type="number" value={form.budget_critical_percent} onChange={(e) => set("budget_critical_percent")(e.target.value)} /></div>
            </div>
          </div>

          <div className="border-t pt-3 grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Low-balance threshold (warning)</Label>
              <Input type="number" value={form.low_balance_threshold} onChange={(e) => set("low_balance_threshold")(e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Critical balance threshold</Label>
              <Input type="number" value={form.critical_balance_threshold} onChange={(e) => set("critical_balance_threshold")(e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Low remaining % (warning)</Label>
              <Input type="number" value={form.low_remaining_percent_threshold} onChange={(e) => set("low_remaining_percent_threshold")(e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Critical remaining %</Label>
              <Input type="number" value={form.critical_remaining_percent_threshold} onChange={(e) => set("critical_remaining_percent_threshold")(e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Warning days before renewal</Label>
              <Input type="number" value={form.warning_days_before} onChange={(e) => set("warning_days_before")(e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Critical days before renewal</Label>
              <Input type="number" value={form.critical_days_before} onChange={(e) => set("critical_days_before")(e.target.value)} /></div>
          </div>

          <div className="border-t pt-3 space-y-2">
            <p className="text-xs font-medium">Manual data freshness</p>
            <div className="grid sm:grid-cols-2 gap-3 items-end">
              <div className="space-y-1"><Label className="text-xs">Mark manual data stale after (days)</Label>
                <Input data-testid="billing-edit-stale-days" type="number" value={form.manual_stale_after_days} onChange={(e) => set("manual_stale_after_days")(e.target.value)} /></div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" data-testid="billing-edit-confirm-manual" checked={form.confirm_manual_data}
                  onChange={(e) => set("confirm_manual_data")(e.target.checked)} />
                The manual figures above are current (refreshes the "last updated" stamp)
              </label>
            </div>
          </div>

          <div className="space-y-1"><Label className="text-xs">Status override</Label>
            <Select value={form.status_override || "__none"} onValueChange={(v) => set("status_override")(v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="billing-edit-status-override"><SelectValue placeholder="Computed automatically" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Computed automatically</SelectItem>
                <SelectItem value="payment_issue">Payment issue (mark manually)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">Only "Payment issue" can be forced manually — every other status is always computed from balance/usage/budget/dates.</p>
          </div>
          <div className="space-y-1"><Label className="text-xs">Billing/account portal URL</Label>
            <Input value={form.billing_url} onChange={(e) => set("billing_url")(e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes")(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="billing-edit-save" onClick={save}>Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Railway's card shows a few fields no other provider has (billing-period
   start, workspace state). The Railway "creditBalance" is UNUSED PROMOTIONAL
   credits (stabilization round correction) — never shown as a balance or
   remaining capacity; see provider_billing.fetch_railway_billing. The generic
   "Renewal" row is skipped for Railway (its billing period just rolls). */
const RailwayExtraRows = ({ doc }) => {
  const snap = doc.provider_snapshot || {};
  if (doc.provider !== "railway") return null;
  return (
    <>
      <span className="text-muted-foreground">Billing period starts</span>
      <span data-testid="billing-railway-period-start">{snap.billing_period_start ? fmtIST(snap.billing_period_start, { year: true }) : "—"}</span>
      <span className="text-muted-foreground">Billing period ends</span>
      <span data-testid="billing-railway-period-end">{snap.billing_period_end ? fmtIST(snap.billing_period_end, { year: true }) : "—"}</span>
      <span className="text-muted-foreground">Workspace state</span>
      <span data-testid="billing-railway-state">{snap.workspace_state || "—"}</span>
    </>
  );
};

const Row = ({ label, children, testid }) => (
  <>
    <span className="text-muted-foreground">{label}</span>
    <span data-testid={testid}>{children}</span>
  </>
);

export const ProviderCard = ({ doc, onEdit, onRefresh, refreshing, onAcknowledge }) => {
  const a = doc.assessment || {};
  const integ = a.integration || {};
  const data = a.data || {};
  const manual = data.manual || {};
  const plan = a.plan || {};
  const spend = a.spend || {};
  const quota = a.quota || {};
  const balance = a.balance || {};
  const promo = a.promotional_credits || {};
  const state = stateOf(doc);
  const cur = plan.currency || doc.currency;
  const pct = fmtPercent(quota.used, quota.limit);
  const isRailway = doc.provider === "railway";
  const conditions = doc.alert_conditions || [];
  const provides = (a.capabilities || {}).remaining_balance;
  return (
    <Card data-testid={`billing-provider-${doc.provider}`}>
      <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-sm font-heading">{doc.display_name}</CardTitle>
        <Badge variant="outline" className={`border-0 text-[10px] ${STATE_TONE[state] || STATE_TONE.unknown_no_data}`} data-testid={`billing-status-${doc.provider}`}>
          {STATE_LABEL[state]}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-1.5 text-xs">
        {doc.state_explanation && (
          <p className="text-[11px] text-muted-foreground" data-testid={`billing-explain-${doc.provider}`}>{doc.state_explanation}</p>
        )}
        {/* "Unknown - no budget set" is a one-piece-of-config-away gap, never a
            provider failure (see state_explanation above) - make it directly
            actionable rather than leaving the admin to guess which of the many
            fields below to fill in. Confirmed production evidence: Railway sits
            here whenever usage is available but no budget has been entered. */}
        {state === "unknown_no_budget" && (
          <Button size="sm" variant="outline" className="h-6 text-[11px]" data-testid={`billing-set-budget-${doc.provider}`}
            onClick={() => onEdit(doc)}>
            Set a monthly budget →
          </Button>
        )}
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          <Row label="Integration" testid={`billing-integration-${doc.provider}`}>{INTEGRATION_LABEL[integ.health] || integ.health || "—"}</Row>
          <Row label="Credential requires" testid={`billing-required-scope-${doc.provider}`}>
            {integ.required_scopes?.length
              ? integ.required_scopes.join(", ")
              : (integ.health === "not_applicable" || integ.health === "manual_mode"
                ? "No credential - manually tracked"
                : "No special permission scope")}
          </Row>
          <Row label="Data" testid={`billing-source-${doc.provider}`}>
            {(data.availability === "available" ? "Available" : "Not available")} · {SOURCE_LABEL[data.source] || "—"}
          </Row>
          {data.source === "manual" && (
            <Row label="Manual data" testid={`billing-manual-${doc.provider}`}>
              {manual.updated_at ? `updated ${fmtIST(manual.updated_at)}${manual.updated_by_name ? ` by ${manual.updated_by_name}` : ""}` : "no update time recorded"}
              {freshnessText(manual) && (
                <span className={manual.freshness === "stale" ? " text-amber-600 dark:text-amber-400" : " text-muted-foreground"}> · {freshnessText(manual)}</span>
              )}
            </Row>
          )}
          <Row label="Plan">{doc.plan_name || "—"}</Row>
          <Row label="Monthly budget" testid={`billing-budget-${doc.provider}`}>
            {plan.budget_configured
              ? `${fmtMoney(plan.monthly_budget, cur)} (warn at ${plan.budget_warning_percent ?? 80}%)`
              : <span className="text-muted-foreground">not set</span>}
          </Row>
          <Row label="Auto-renew">{doc.auto_renew === true ? "ON" : doc.auto_renew === false ? "OFF" : "Unknown"}</Row>
          <Row label={isRailway || doc.provider === "openai" ? "This period spend" : "Spend / cost"} testid={`billing-spend-${doc.provider}`}>
            {fmtMoney(spend.current ?? doc.monthly_cost, cur)}
            {spend.budget_used_percent != null && <span className="text-muted-foreground"> ({spend.budget_used_percent}% of budget)</span>}
          </Row>
          {spend.estimated_month_end != null && (
            <Row label="Est. month-end" testid={`billing-estimate-${doc.provider}`}>
              {fmtMoney(spend.estimated_month_end, cur)} <span className="text-muted-foreground">(projection of real spend)</span>
            </Row>
          )}
          <Row label="Remaining balance" testid={`billing-remaining-${doc.provider}`}>
            {balance.remaining != null
              ? <>{fmtMoney(balance.remaining, cur)}{balance.is_estimated && <span className="text-muted-foreground"> (estimated from manual prepaid credit)</span>}</>
              : <span className="text-muted-foreground">{provides ? "not reported" : "not provided by this provider"}</span>}
          </Row>
          {(promo.meaning || promo.amount != null) && (
            <Row label="Promotional credits" testid={`billing-promo-${doc.provider}`}>
              {fmtMoney(promo.amount, cur)} <span className="text-muted-foreground">(unused credits, not capacity)</span>
            </Row>
          )}
          {quota.limit != null && (
            <Row label="Usage">{quota.used?.toLocaleString?.() ?? quota.used} / {quota.limit?.toLocaleString?.()} {pct != null && `(${pct}% remaining)`}</Row>
          )}
          {!isRailway && doc.provider !== "openai" && <Row label="Renewal">{doc.renewal_date ? fmtIST(doc.renewal_date, { year: true, time: false }) : "—"}</Row>}
          {doc.provider === "openai" && doc.renewal_date && <Row label="Budget cycle renews">{fmtIST(doc.renewal_date, { year: true, time: false })}</Row>}
          {doc.validity_date && <Row label="Validity">{fmtIST(doc.validity_date, { year: true, time: false })}</Row>}
          <RailwayExtraRows doc={doc} />
          <Row label="Last checked">{doc.last_checked_at ? fmtIST(doc.last_checked_at, { zone: true }) : "never"}</Row>
          <Row label="Last success">{doc.last_success_at ? fmtIST(doc.last_success_at, { zone: true }) : "never"}</Row>
        </div>
        {promo.meaning && (
          <p className="text-muted-foreground text-[11px] pt-0.5" data-testid="billing-railway-credit-meaning">{promo.meaning}</p>
        )}
        {a.coverage_note && (
          <p className="text-muted-foreground text-[11px] pt-0.5" data-testid={`billing-coverage-${doc.provider}`}>
            {coverageText(a)} {a.coverage_note}
          </p>
        )}
        {integ.message && (
          <p className="text-[11px] pt-1 text-orange-600 dark:text-orange-400" data-testid={`billing-integration-error-${doc.provider}`}>
            {integ.message}
          </p>
        )}
        {(doc.last_error && !integ.message) && <p className="text-[hsl(var(--danger))] text-[11px] pt-1">{doc.last_error}</p>}
        {doc.notes && <p className="text-muted-foreground text-[11px] pt-1 italic">{doc.notes}</p>}
        {conditions.length > 0 && (
          <div className="border-t pt-1.5 space-y-1" data-testid={`billing-conditions-${doc.provider}`}>
            <p className="text-[11px] font-medium">Active alert conditions</p>
            {conditions.map((c) => (
              <div key={c.key} className="flex items-start justify-between gap-2 text-[11px]">
                <span>
                  <span className={c.level === "critical" ? "text-[hsl(var(--danger))]" : "text-amber-600 dark:text-amber-400"}>{c.level}</span>
                  {" - "}{c.message}
                  {c.acknowledged
                    ? <span className="text-muted-foreground"> · acknowledged{c.acknowledged_by_name ? ` by ${c.acknowledged_by_name}` : ""}{c.acknowledged_at ? ` ${fmtIST(c.acknowledged_at)}` : ""} - no further notifications</span>
                    : c.next_reminder_at && <span className="text-muted-foreground"> · next reminder no earlier than {fmtIST(c.next_reminder_at)}</span>}
                </span>
              </div>
            ))}
            {conditions.some((c) => !c.acknowledged) && (
              <Button size="sm" variant="outline" className="h-6 text-[11px]" data-testid={`billing-ack-${doc.provider}`}
                onClick={() => onAcknowledge && onAcknowledge(doc.provider)}>
                <Check className="h-3 w-3 mr-1" />Acknowledge (keep visible, stop notifications)
              </Button>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-2 border-t mt-1">
          {doc.billing_url && (
            <a href={doc.billing_url} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" className="h-7 text-xs"><ExternalLink className="h-3.5 w-3.5 mr-1" />Portal</Button>
            </a>
          )}
          <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`billing-refresh-${doc.provider}`}
            disabled={refreshing} onClick={() => onRefresh(doc.provider)}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />Refresh
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs ml-auto" data-testid={`billing-edit-${doc.provider}`} onClick={() => onEdit(doc)}>
            <Pencil className="h-3.5 w-3.5 mr-1" />Configure
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const E164_RE = /^\+[1-9]\d{6,14}$/;

/* Alert recipients. Persistence contract (the reported "typed but not added"
   bug): the list shown is ONLY what the server confirmed it stored — every
   add goes through POST /recipients/add, is validated server-side, and the
   response is the stored list. Enter submits (it used to do nothing); a
   rejected value keeps an inline, persistent error next to the field instead
   of a toast that vanishes; a typed-but-unsaved value is called out. */
export const RecipientsCard = () => {
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [drafts, setDrafts] = useState({ email: "", whatsapp: "" });
  const [errors, setErrors] = useState({ email: null, whatsapp: null, whatsapp_toggle: null });
  const [busy, setBusy] = useState(false);
  const [hours, setHours] = useState("");
  const load = useCallback(() => api.get("/admin/billing/recipients").then((r) => {
    setConfig(r.data); setLoadError(null); setHours(String(r.data.reminder_repeat_hours ?? 24));
  }).catch((e) => setLoadError(errDetail(e))), []);
  useEffect(() => { load(); }, [load]);

  const setErr = (k, v) => setErrors((e) => ({ ...e, [k]: v }));

  const add = async (channel) => {
    const raw = drafts[channel].trim();
    if (!raw) { setErr(channel, "Enter a value first."); return; }
    if (channel === "email" && !EMAIL_RE.test(raw)) { setErr(channel, "Enter a valid email address, e.g. finance@company.com"); return; }
    if (channel === "whatsapp" && !E164_RE.test(raw.replace(/[\s\-().]/g, ""))) { setErr(channel, "Use E.164 format, e.g. +919876543210"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/billing/recipients/add", { channel, value: raw });
      setConfig(data);
      setDrafts((d) => ({ ...d, [channel]: "" }));
      setErr(channel, null);
      toast.success(data.already_present ? "Already in the list" : "Recipient saved");
    } catch (e) { setErr(channel, errDetail(e)); } finally { setBusy(false); }
  };
  const remove = async (channel, value) => {
    try {
      const { data } = await api.post("/admin/billing/recipients/remove", { channel, value });
      setConfig(data);
    } catch (e) { toast.error(errDetail(e)); }
  };
  const toggleWhatsapp = async (enabled) => {
    setErr("whatsapp_toggle", null);
    try {
      const { data } = await api.put("/admin/billing/recipients", { whatsapp_enabled: enabled, recipients: config.recipients });
      setConfig(data);
    } catch (e) { setErr("whatsapp_toggle", errDetail(e)); }
  };
  const saveHours = async () => {
    try {
      const { data } = await api.put("/admin/billing/recipients", {
        whatsapp_enabled: config.whatsapp_enabled, recipients: config.recipients, reminder_repeat_hours: Number(hours),
      });
      setConfig(data); toast.success("Reminder interval saved");
    } catch (e) { toast.error(errDetail(e)); }
  };
  const sendTest = async (channel) => {
    try {
      const { data } = await api.post("/admin/billing/alerts/test", { channel });
      toast.success(channel === "email"
        ? `TEST email queued for ${data.queued} recipient(s) - it is not sent unless TEST sends are enabled`
        : "TEST in-app alert sent to you only");
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (loadError) {
    return (
      <Card><CardContent className="pt-4 text-xs space-y-2" data-testid="billing-recipients-load-error">
        <p className="text-[hsl(var(--danger))]">Could not load alert recipients: {loadError}</p>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={load}>Retry</Button>
      </CardContent></Card>
    );
  }
  if (!config) return <Skeleton className="h-32" />;
  const empty = config.recipients.length === 0 && config.email_recipients.length === 0;
  // Plain render functions (NOT components): a component defined inside render
  // would remount on every keystroke and steal focus from the input.
  const chip = (channel, value) => (
    <Badge key={value} variant="outline" className="text-xs gap-1" data-testid={`billing-recipient-${channel}`}>{value}
      <button type="button" aria-label={`Remove ${value}`} onClick={() => remove(channel, value)} className="ml-1"><X className="h-3 w-3" /></button>
    </Badge>
  );
  const addRow = (channel, placeholder, type) => (
    <form className="space-y-1" onSubmit={(e) => { e.preventDefault(); add(channel); }}>
      <div className="flex gap-2">
        <Input data-testid={`billing-${channel === "email" ? "email" : "wa"}-recipient-input`} type={type} className="h-8 text-xs"
          placeholder={placeholder} value={drafts[channel]}
          onChange={(e) => { setDrafts((d) => ({ ...d, [channel]: e.target.value })); setErr(channel, null); }} />
        <Button type="submit" size="sm" className="h-8 text-xs" disabled={busy}
          data-testid={`billing-${channel === "email" ? "email" : "wa"}-recipient-add`}><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
      </div>
      {errors[channel] && <p className="text-[11px] text-[hsl(var(--danger))]" role="alert" data-testid={`billing-recipient-error-${channel}`}>{errors[channel]}</p>}
      {!errors[channel] && drafts[channel].trim() && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid={`billing-recipient-unsaved-${channel}`}>Not added yet - press Add (or Enter) to save it.</p>
      )}
    </form>
  );
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Billing Alert Recipients</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs">In-app Admin alert</Label>
          <span className="text-xs text-muted-foreground">Always ON</span>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Email recipients</Label>
          <div className="flex flex-wrap gap-1.5">{config.email_recipients.map((v) => chip("email", v))}</div>
          {addRow("email", "name@company.com", "email")}
          {!config.email_transport_enabled && config.email_recipients.length > 0 && (
            <p className="text-[11px] text-muted-foreground">The Email integration is not connected, so alert emails are queued but not delivered yet; the in-app alert is always sent.</p>
          )}
        </div>

        <div className="space-y-1.5 border-t pt-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">WhatsApp alerts</Label>
            <Switch data-testid="billing-wa-enabled" checked={config.whatsapp_enabled}
              disabled={!config.whatsapp_template_ready && !config.whatsapp_enabled}
              onCheckedChange={toggleWhatsapp} />
          </div>
          {!config.whatsapp_template_ready && (
            <p className="text-[11px] text-muted-foreground" data-testid="billing-wa-template-blocked">
              Off until the "billing_alert" WhatsApp template is mapped to an approved Interakt template
              (Administration → Integrations). {config.whatsapp_enabled ? "It is currently enabled but nothing will be sent." : ""}
            </p>
          )}
          {errors.whatsapp_toggle && <p className="text-[11px] text-[hsl(var(--danger))]" role="alert">{errors.whatsapp_toggle}</p>}
          <div className="flex flex-wrap gap-1.5">{config.recipients.map((v) => chip("whatsapp", v))}</div>
          {addRow("whatsapp", "+919876543210", "text")}
        </div>

        {empty && (
          <p className="text-xs text-muted-foreground" data-testid="billing-recipients-empty">
            No email or WhatsApp recipients configured yet. Admin users still receive every alert in-app.
          </p>
        )}

        <div className="border-t pt-2 space-y-1.5">
          <Label className="text-xs">Reminder policy</Label>
          <p className="text-[11px] text-muted-foreground">{config.reminder_policy}</p>
          <div className="flex items-center gap-2">
            <Input className="h-8 w-20 text-xs" type="number" min="1" max="720" value={hours} data-testid="billing-reminder-hours"
              onChange={(e) => setHours(e.target.value)} />
            <span className="text-[11px] text-muted-foreground">hours between "Still critical" reminders</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={saveHours}
              disabled={Number(hours) === config.reminder_repeat_hours}>Save</Button>
          </div>
        </div>

        <div className="border-t pt-2 flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-muted-foreground">Safe test alerts (never contact a provider):</span>
          <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="billing-test-inapp" onClick={() => sendTest("in_app")}>In-app (to me)</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="billing-test-email" onClick={() => sendTest("email")}
            disabled={config.email_recipients.length === 0}>Email (TEST-tagged)</Button>
        </div>
      </CardContent>
    </Card>
  );
};

/* Kept permanently (2026-09 round 3) as a safe, read-only connection test
   for troubleshooting — e.g. a future "Not Authorized" after a token is
   rotated. Runs the exact same fixed query the real Railway card itself
   now uses (backend: provider_billing.railway_diagnostic_check / the same
   query as fetch_railway_billing) — never a general-purpose GraphQL
   console, never writes anything to the stored provider doc. */
const RailwayDiagnostic = () => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/billing/providers/railway/diagnostic");
      setResult(data);
      toast[data.ok ? "success" : "warning"](data.ok
        ? `Diagnostic OK — authenticated as ${data.authenticated_as}`
        : `Diagnostic failed: ${data.error}`);
    } catch (e) { toast.error(errDetail(e)); } finally { setRunning(false); }
  };

  return (
    <div className="border-t pt-2 mt-1 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
          <FlaskConical className="h-3 w-3" />Connection test — verifies credentials, changes nothing
        </p>
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={running}
          data-testid="billing-railway-diagnostic-run" onClick={run}>
          {running ? "Running…" : "Test connection"}
        </Button>
      </div>
      {result && (
        <div className="rounded-md border p-2 text-[11px] space-y-1.5" data-testid="billing-railway-diagnostic-result">
          <p className={result.ok ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}>
            {result.ok ? `Succeeded — authenticated as ${result.authenticated_as}` : `Failed — ${result.error}`}
          </p>
          {!result.ok && result.token_scope_hint && (
            <p className="text-muted-foreground" data-testid="billing-railway-diagnostic-scope-hint">
              {result.token_scope_hint}
            </p>
          )}
          {result.ok && (
            <div className="grid grid-cols-[auto_auto_1fr] gap-x-3 gap-y-0.5 text-muted-foreground">
              {Object.entries(result.fields).map(([name, f]) => (
                <Fragment key={name}>
                  <span className="text-foreground">{name}</span>
                  <span>{f.present ? f.type : "missing"}</span>
                  <span className="truncate">{f.present ? JSON.stringify(f.value) : "—"}</span>
                </Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RailwayTokenCard = () => {
  const [info, setInfo] = useState(null);
  const [value, setValue] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const load = useCallback(() => api.get("/admin/billing/railway-token").then((r) => {
    setInfo(r.data); setWorkspaceId(r.data.workspace_id || "");
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      await api.put("/admin/billing/railway-token", { token: value });
      toast.success("Railway token saved");
      setValue(""); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const saveWorkspaceId = async () => {
    try {
      await api.put("/admin/billing/railway-token", { workspace_id: workspaceId });
      toast.success("Railway Workspace ID saved");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  if (!info) return null;
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Railway API Token</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <p className="text-[11px] text-muted-foreground">Used only to check Railway's own billing/usage GraphQL API — never exposed to the browser beyond a last-4 fingerprint. Prefer the smallest-scope token Railway allows — a Workspace token works and needs no broader Account access, as long as the Workspace ID below is also set.</p>
        <p className="text-xs">{info.configured ? `Configured (${info.masked})` : "Not configured"}</p>
        <div className="flex gap-2">
          <Input data-testid="billing-railway-token-input" type="password" className="h-8 text-xs" placeholder="Paste Railway token…" value={value} onChange={(e) => setValue(e.target.value)} />
          <Button size="sm" className="h-8 text-xs" disabled={!value.trim()} onClick={save}>Save</Button>
        </div>
        <div className="space-y-1 pt-1">
          <Label className="text-xs">Railway Workspace ID</Label>
          <p className="text-[11px] text-muted-foreground">Not a secret — just says which workspace to read. Required for a Workspace-scoped token, which can't discover it on its own.</p>
          <div className="flex gap-2">
            <Input data-testid="billing-railway-workspace-id-input" className="h-8 text-xs" placeholder="Workspace ID…" value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} />
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={workspaceId === (info.workspace_id || "")} onClick={saveWorkspaceId}>Save</Button>
          </div>
        </div>
        {info.configured && <RailwayDiagnostic />}
      </CardContent>
    </Card>
  );
};

/* ElevenLabs billing-monitor credential (round 9) — a credential SEPARATE
   from the Voice operational key (configured in Administration → Voice &
   ElevenLabs) by default, because they are a genuinely different concern
   (reading subscription/usage vs. placing calls); an admin may instead
   explicitly opt into reusing the Voice credential for billing reads too.
   Never shows the full secret after saving - only a last-4 fingerprint,
   read back from the server on every load/save. "Test connection" only
   ever reads ElevenLabs' subscription/usage endpoint - it can never place
   a call or change provider state. */
export const ElevenLabsCredentialCard = () => {
  const [info, setInfo] = useState(null);
  const [value, setValue] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const load = useCallback(() => api.get("/admin/billing/elevenlabs-credential").then((r) => {
    setInfo(r.data);
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put("/admin/billing/elevenlabs-credential", { api_key: value });
      toast.success("ElevenLabs billing-monitor credential saved");
      setValue(""); setTestResult(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const setSharedMode = async (useShared) => {
    try {
      await api.put("/admin/billing/elevenlabs-credential", { use_shared_voice_credential: useShared });
      toast.success(useShared ? "Now reusing the Voice credential for billing monitoring" : "Now using a dedicated billing-monitor credential");
      setTestResult(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/billing/elevenlabs-credential/test-connection");
      setTestResult(data);
      toast[data.ok ? "success" : "warning"](data.ok ? "Connection succeeded" : `Connection failed: ${data.error_code}`);
    } catch (e) { toast.error(errDetail(e)); } finally { setTesting(false); }
  };

  if (!info) return null;
  const shared = info.mode === "shared_voice";
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">ElevenLabs Billing-Monitor Credential</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-muted-foreground">
          Used only to read ElevenLabs' own subscription/usage endpoint for billing monitoring - never to place a
          call. Needs an API key with the <code>user_read</code> permission. Distinct from the Voice operational
          credential (Administration → Voice &amp; ElevenLabs) unless you explicitly choose to reuse it below.
        </p>

        <div className="flex items-center justify-between border-b pb-2">
          <div>
            <Label className="text-xs">Reuse the Voice credential for billing monitoring</Label>
            <p className="text-[11px] text-muted-foreground">
              {info.voice_credential_configured ? `Voice credential: ${info.voice_credential_masked}` : "No Voice credential is configured yet."}
            </p>
          </div>
          <Switch data-testid="billing-el-shared-toggle" checked={shared} onCheckedChange={setSharedMode} />
        </div>

        {!shared && (
          <div className="space-y-1.5" data-testid="billing-el-dedicated-section">
            <Label className="text-xs">Dedicated billing-monitor API key</Label>
            <p className="text-xs" data-testid="billing-el-dedicated-status">
              {info.dedicated_credential_configured ? `Configured (${info.dedicated_credential_masked})` : "Not configured"}
            </p>
            <div className="flex gap-2">
              <Input data-testid="billing-el-key-input" type="password" className="h-8 text-xs"
                placeholder="Paste ElevenLabs billing-monitor API key…" value={value}
                onChange={(e) => setValue(e.target.value)} />
              <Button size="sm" className="h-8 text-xs" disabled={!value.trim()} data-testid="billing-el-key-save" onClick={save}>Save</Button>
            </div>
          </div>
        )}
        {shared && (
          <p className="text-[11px] text-muted-foreground" data-testid="billing-el-shared-note">
            Billing monitoring now uses the same credential as Voice calling. Saving a dedicated key above is
            disabled while this is on - turn it off to configure a separate billing-only credential instead.
          </p>
        )}

        <div className="flex items-center justify-between pt-1 border-t">
          <p className="text-[11px] text-muted-foreground flex items-center gap-1">
            <FlaskConical className="h-3 w-3" />Test connection - reads subscription/usage only, never places a call
          </p>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={testing || !info.effective_credential_configured}
            data-testid="billing-el-test-connection" onClick={testConnection}>
            {testing ? "Testing…" : "Test connection"}
          </Button>
        </div>
        {testResult && (
          <div className="rounded-md border p-2 text-[11px] space-y-1" data-testid="billing-el-test-result">
            {testResult.ok ? (
              <p className="text-[hsl(var(--success))]">Succeeded{testResult.plan_name ? ` — plan: ${testResult.plan_name}` : ""}</p>
            ) : (
              <>
                <p className="text-[hsl(var(--danger))]">Failed ({testResult.error_code}): {testResult.error}</p>
                {testResult.error_code === "missing_scope" && (
                  <p className="text-amber-600 dark:text-amber-400" data-testid="billing-el-test-missing-scope">
                    Missing permission: {testResult.missing_scopes.join(", ")}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const BillingSubscriptionsTab = () => {
  const [providers, setProviders] = useState(null);
  const [events, setEvents] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [refreshingProvider, setRefreshingProvider] = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);

  const loadProviders = useCallback(() => api.get("/admin/billing/providers").then((r) => setProviders(r.data || [])).catch(() => setProviders([])), []);
  const loadEvents = useCallback(() => api.get("/admin/billing/events").then((r) => setEvents(r.data || [])).catch(() => {}), []);
  const loadSchedulerStatus = useCallback(() => api.get("/admin/billing/scheduler-status").then((r) => setSchedulerStatus(r.data)).catch(() => {}), []);

  useEffect(() => { loadProviders(); loadEvents(); loadSchedulerStatus(); }, [loadProviders, loadEvents, loadSchedulerStatus]);

  const refreshOne = async (provider) => {
    setRefreshingProvider(provider);
    try {
      await api.post(`/admin/billing/providers/${provider}/refresh`);
      toast.success(`${provider} refreshed`);
      loadProviders(); loadEvents(); loadSchedulerStatus();
    } catch (e) { toast.error(errDetail(e)); } finally { setRefreshingProvider(null); }
  };
  const acknowledge = async (provider) => {
    try {
      await api.post(`/admin/billing/providers/${provider}/acknowledge`, {});
      toast.success("Acknowledged - the condition stays visible but no further notifications are sent");
      loadProviders();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const checkAll = async () => {
    setCheckingAll(true);
    try {
      await api.post("/admin/billing/check-all");
      toast.success("All providers checked");
      loadProviders(); loadEvents(); loadSchedulerStatus();
    } catch (e) { toast.error(errDetail(e)); } finally { setCheckingAll(false); }
  };

  if (!providers) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4" data-testid="billing-subscriptions-admin">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Tracks billing/subscription health for the external services SalesOS depends on. Every provider supports
          full manual tracking; automatic checks only enrich the fields each provider's own API genuinely exposes.
        </p>
        <Button size="sm" disabled={checkingAll} onClick={checkAll} data-testid="billing-check-all">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${checkingAll ? "animate-spin" : ""}`} />
          {checkingAll ? "Checking…" : "Check all now"}
        </Button>
      </div>

      {schedulerStatus && (
        <Card>
          <CardContent className="pt-3 pb-3 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Last scheduled run: {schedulerStatus.last_scheduled_run_at ? fmtIST(schedulerStatus.last_scheduled_run_at, { zone: true }) : "never"}</span>
            <span>Next scheduled run: {schedulerStatus.next_scheduled_run_at ? fmtIST(schedulerStatus.next_scheduled_run_at, { zone: true }) : "—"}</span>
            <span>Last manual check: {schedulerStatus.last_manual_check_at ? fmtIST(schedulerStatus.last_manual_check_at, { zone: true }) : "never"}</span>
            <span className="flex items-center gap-1"><ShieldAlert className="h-3 w-3" />Scheduler heartbeat: {schedulerStatus.scheduler_heartbeat_at ? fmtIST(schedulerStatus.scheduler_heartbeat_at, { zone: true }) : "unknown"}</span>
          </CardContent>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {providers.map((p) => (
          <ProviderCard key={p.provider} doc={p} onEdit={setEditingDoc} onRefresh={refreshOne}
            onAcknowledge={acknowledge} refreshing={refreshingProvider === p.provider} />
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <RecipientsCard />
        <RailwayTokenCard />
        <ElevenLabsCredentialCard />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-1.5"><History className="h-3.5 w-3.5" />Recent activity</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Provider</TableHead><TableHead>Event</TableHead><TableHead>Detail</TableHead></TableRow></TableHeader>
              <TableBody>
                {events.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground text-sm py-6">No activity yet</TableCell></TableRow>}
                {events.map((e) => (
                  <TableRow key={e.event_id}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtIST(e.created_at, { zone: true })}</TableCell>
                    <TableCell className="text-xs">{e.provider}</TableCell>
                    <TableCell className="text-xs">{e.event_type.replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-md truncate">{e.detail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <EditProviderDialog doc={editingDoc} onClose={() => setEditingDoc(null)}
        onSaved={() => { setEditingDoc(null); loadProviders(); loadEvents(); }} />
    </div>
  );
};
